import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

interface HelpTooltipProps {
  helpKey: string;
  ns?: string;
  tOptions?: Record<string, string>;
  size?: 'sm' | 'md';
  className?: string;
}

export default function HelpTooltip({
  helpKey,
  ns = 'help',
  tOptions,
  size = 'sm',
  className,
}: HelpTooltipProps) {
  const { t } = useTranslation(ns);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const title = t(`${helpKey}.title`, tOptions);
  const description = t(`${helpKey}.description`, tOptions);
  const bullets = t(`${helpKey}.bullets`, { returnObjects: true, defaultValue: [] }) as string[];
  const hasBullets = Array.isArray(bullets) && bullets.length > 0;

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < 200;

    const popoverWidth = 256; // w-64
    // Position absolue dans le document (viewport + scroll)
    let left = rect.left + window.scrollX + rect.width / 2 - popoverWidth / 2;
    // Clamp aux bords du viewport
    const viewportLeft = window.scrollX + 8;
    const viewportRight = window.scrollX + window.innerWidth - 8 - popoverWidth;
    if (left < viewportLeft) left = viewportLeft;
    if (left > viewportRight) left = viewportRight;

    setPopoverStyle({
      position: 'absolute',
      top: above ? undefined : rect.bottom + window.scrollY + 8,
      bottom: above ? document.documentElement.scrollHeight - (rect.top + window.scrollY) + 8 : undefined,
      left,
      width: popoverWidth,
    });
  }, []);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isOpen) {
      computePosition();
    }

    setIsOpen((prev) => !prev);
  };

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <span className={clsx('relative inline-flex items-center', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
        aria-label={title}
      >
        <HelpCircle className={iconSize} />
      </button>

      {isOpen && createPortal(
        <div
          ref={popoverRef}
          style={popoverStyle}
          className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4"
        >
          <p className="text-sm font-semibold text-gray-900 mb-1">{title}</p>
          <p className="text-xs text-gray-600 leading-relaxed">{description}</p>

          {hasBullets && (
            <ul className="text-xs text-gray-600 mt-2 space-y-1 list-disc list-inside">
              {bullets.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}
